/* eslint-disable */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Session 57 · Alpha Dashboard.
 *
 * A single-glance command center for the Founding Member / Early Adopter
 * launch phase. Numbers-first, no fluff.
 */
export default function AdminAlphaDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/founding-members/stats");
        setStats(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (!user || user.role !== "admin") {
    return <div style={{ padding: 40 }}>Admin access required.</div>;
  }

  return (
    <div data-testid="admin-alpha-dashboard" style={{ minHeight: "100vh", background: "#FAF8F3", padding: "40px 24px 80px", fontFamily: "Helvetica,Arial,sans-serif", color: "#1B2340" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
          <div>
            <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700 }}>MÉTIER · ADMIN</div>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 34, margin: "6px 0 0" }}>Alpha Dashboard</h1>
            <p style={{ fontSize: 14, color: "rgba(27,35,64,0.6)", margin: "6px 0 0" }}>
              Launch metrics for the 100-seat Founding Member cohort.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Link to="/admin/founding-members" style={ghostLink}>Founding Members →</Link>
            <Link to="/admin/command-center" style={ghostLink}>Command Center →</Link>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>Loading…</div>
        ) : (
          <>
            {/* Big number: cohort progress */}
            <div style={{
              background: "linear-gradient(135deg,#1B2340 0%,#2A3560 100%)",
              color: "#fff",
              borderRadius: 16,
              padding: "30px 34px",
              marginBottom: 22,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: 24,
            }}>
              <BigStat label="Founding Members" value={`${stats.founding_members} / ${stats.cohort_cap}`} sub={`${stats.seats_remaining} seats remaining`} />
              <BigStat label="Members" value={stats.early_adopters} sub="Post-invitation signups" />
              <BigStat label="Pending Invites" value={stats.pending} sub="Awaiting first login" />
              <BigStat label="Profiles Completed" value={stats.profiles_complete} sub={`${stats.profile_completion_pct}% completion rate`} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
              <Section title="Activation Funnel">
                <FunnelRow label="Invitations sent" value={stats.invitations_sent} />
                <FunnelRow label="Invitations accepted" value={stats.invitations_accepted} suffix={`${stats.acceptance_rate_pct}%`} />
                <FunnelRow label="Password created + logged in" value={stats.activated} suffix={`${stats.activation_rate_pct}%`} />
                <FunnelRow label="Profile completed" value={stats.profiles_complete} suffix={`${stats.profile_completion_pct}%`} />
              </Section>

              <Section title="Engagement">
                <FunnelRow label="First booking" value={`${stats.first_booking_pct}%`} sub="of activated members" />
                <FunnelRow label="First Commons post" value={`${stats.first_commons_post_pct}%`} sub="of activated members" />
                <FunnelRow label="First Métier Hours visit" value={`${stats.first_hours_visit_pct}%`} sub="of activated members" />
                <FunnelRow label="Feedback submitted" value={`${stats.feedback_submitted_pct}%`} sub="of activated members" />
              </Section>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 20 }}>
              <StatCard label="Bookings" value={stats.bookings_total} />
              <StatCard label="7-day retention" value={stats.retention_7d} sub="active in last 7d" />
              <StatCard label="30-day retention" value={stats.retention_30d} sub="active in last 30d" />
            </div>

            <div style={{ marginTop: 22, background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", padding: "16px 20px", fontSize: 13, color: "rgba(27,35,64,0.7)" }}>
              <strong style={{ color: "#B8913A" }}>Safety guardrails:</strong> cleanup script always dry-runs first · admins + real venue owners + production venues protected · founding cohort permanently capped at {stats.cohort_cap}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BigStat({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 38, fontWeight: 500, marginTop: 6, color: "#F5E9CC" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: "#fff", padding: "18px 20px", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 28, fontWeight: 500, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "rgba(27,35,64,0.6)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", padding: "18px 20px" }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B8913A", fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function FunnelRow({ label, value, suffix, sub }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "10px 0", borderBottom: "1px dashed rgba(27,35,64,0.08)" }}>
      <div>
        <div style={{ fontSize: 14 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "rgba(27,35,64,0.55)" }}>{sub}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 500 }}>{value}</div>
        {suffix && <div style={{ fontSize: 11, color: "#B8913A", fontWeight: 700 }}>{suffix}</div>}
      </div>
    </div>
  );
}

const ghostLink = { fontSize: 13, color: "#B8913A", textDecoration: "none", fontWeight: 600 };
